// Lifted from useNineSegmentsTableColumns; pure so basic-output can reuse without React/Jotai.

import * as R from 'remeda'

import { type ColumnInfo, compact, nineSegments } from '~/utils'

import {
  type NineSegmentColumnKey as ColumnKey,
  nineSegmentIndicators,
} from './nineSegments'

const widthMap: Record<ColumnKey, string> = {
  questionText: '11em',
  answerNumber: 'max-content',
  answerText: 'max-content',
  ...R.mapToObj(
    ['total', ...nineSegments, ...nineSegmentIndicators] as const,
    (indicator) => [indicator, '7em'],
  ),
  ...R.mapToObj(
    [
      'totalPad',
      'currentUserPad',
      'recognizedPad',
      'estrangedPad',
      'generalPad',
      'loyalPad',
      'recognizedPositiveGeneralPositivePad',
      'recognizedNegativeGeneralPositivePad',
      'estrangedPositiveGeneralPositivePad',
      'estrangedNegativeGeneralPositivePad',
      'generalPositiveLoyalPositivePad',
      'generalNegativeLoyalPositivePad',
      'recognizedNegativeLoyalPositivePad',
      'estrangedNegativeLoyalPositivePad',
    ] as const,
    (indicator) => [indicator, '0.5rem'],
  ),
}

export const buildNineSegmentsTableColumns = ({
  hiddenColumnKeys,
}: {
  hiddenColumnKeys: ReadonlyArray<ColumnKey>
}): ColumnInfo<ColumnKey>[] => {
  const visibleAllOrNineSegments = R.difference(
    R.reverse([...nineSegments, 'total'] satisfies ColumnKey[]),
    hiddenColumnKeys,
  )
  const visibleTotalIndicators = (
    hiddenColumnKeys.includes('totalDiff')
      ? []
      : ['totalNegative', 'totalPositive', 'totalDiff']
  ) satisfies ColumnKey[]

  const visibleCurrentUserIndicators = (
    hiddenColumnKeys.includes('currentUserDiff')
      ? []
      : ['currentUserNegative', 'currentUserPositive', 'currentUserDiff']
  ) satisfies ColumnKey[]
  const visibleRecognizedIndicators = (
    hiddenColumnKeys.includes('recognizedDiff')
      ? []
      : ['recognizedNegative', 'recognizedPositive', 'recognizedDiff']
  ) satisfies ColumnKey[]
  const visibleEstrangedIndicators = (
    hiddenColumnKeys.includes('estrangedDiff')
      ? []
      : ['estrangedNegative', 'estrangedPositive', 'estrangedDiff']
  ) satisfies ColumnKey[]
  const visibleGeneralIndicators = (
    hiddenColumnKeys.includes('generalDiff')
      ? []
      : ['generalNegative', 'generalPositive', 'generalDiff']
  ) satisfies ColumnKey[]
  const visibleLoyalIndicators = (
    hiddenColumnKeys.includes('loyalDiff')
      ? []
      : ['loyalNegative', 'loyalPositive', 'loyalDiff']
  ) satisfies ColumnKey[]
  const visibleRecognizedPositiveGeneralPositiveIndicators = (
    hiddenColumnKeys.includes('recognizedPositiveGeneralPositiveDiff')
      ? []
      : [
          'recognizedPositive',
          'generalPositive',
          'recognizedPositiveGeneralPositiveDiff',
        ]
  ) satisfies ColumnKey[]
  const visibleEstrangedPositiveGeneralPositiveIndicators = (
    hiddenColumnKeys.includes('estrangedPositiveGeneralPositiveDiff')
      ? []
      : [
          'estrangedPositive',
          'generalPositive',
          'estrangedPositiveGeneralPositiveDiff',
        ]
  ) satisfies ColumnKey[]
  const visibleGeneralPositiveLoyalPositiveIndicators = (
    hiddenColumnKeys.includes('generalPositiveLoyalPositiveDiff')
      ? []
      : ['generalPositive', 'loyalPositive', 'generalPositiveLoyalPositiveDiff']
  ) satisfies ColumnKey[]
  const visibleRecognizedNegativeGeneralPositiveIndicators = (
    hiddenColumnKeys.includes('recognizedNegativeGeneralPositiveDiff')
      ? []
      : [
          'recognizedNegative',
          'generalPositive',
          'recognizedNegativeGeneralPositiveDiff',
        ]
  ) satisfies ColumnKey[]
  const visibleEstrangedNegativeGeneralPositiveIndicators = (
    hiddenColumnKeys.includes('estrangedNegativeGeneralPositiveDiff')
      ? []
      : [
          'estrangedNegative',
          'generalPositive',
          'estrangedNegativeGeneralPositiveDiff',
        ]
  ) satisfies ColumnKey[]
  const visibleGeneralNegativeLoyalPositiveIndicators = (
    hiddenColumnKeys.includes('generalNegativeLoyalPositiveDiff')
      ? []
      : ['generalNegative', 'loyalPositive', 'generalNegativeLoyalPositiveDiff']
  ) satisfies ColumnKey[]
  const visibleRecognizedNegativeLoyalPositiveIndicators = (
    hiddenColumnKeys.includes('recognizedNegativeLoyalPositiveDiff')
      ? []
      : [
          'recognizedNegative',
          'loyalPositive',
          'recognizedNegativeLoyalPositiveDiff',
        ]
  ) satisfies ColumnKey[]
  const visibleEstrangedNegativeLoyalPositiveIndicators = (
    hiddenColumnKeys.includes('estrangedNegativeLoyalPositiveDiff')
      ? []
      : [
          'estrangedNegative',
          'loyalPositive',
          'estrangedNegativeLoyalPositiveDiff',
        ]
  ) satisfies ColumnKey[]

  const hasTotalPad =
    0 < visibleAllOrNineSegments.length && 0 < visibleTotalIndicators.length
  const hasCurrentUserPad =
    (0 < visibleAllOrNineSegments.length ||
      0 < visibleTotalIndicators.length) &&
    0 < visibleCurrentUserIndicators.length
  const hasRecognizedPad =
    (0 < visibleAllOrNineSegments.length ||
      0 < visibleTotalIndicators.length ||
      0 < visibleCurrentUserIndicators.length) &&
    0 < visibleRecognizedIndicators.length
  const hasEstrangedPad =
    (0 < visibleAllOrNineSegments.length ||
      0 < visibleTotalIndicators.length ||
      0 < visibleCurrentUserIndicators.length ||
      0 < visibleRecognizedIndicators.length) &&
    0 < visibleEstrangedIndicators.length
  const hasGeneralPad =
    (0 < visibleAllOrNineSegments.length ||
      0 < visibleTotalIndicators.length ||
      0 < visibleCurrentUserIndicators.length ||
      0 < visibleRecognizedIndicators.length ||
      0 < visibleEstrangedIndicators.length) &&
    0 < visibleGeneralIndicators.length
  const hasLoyalPad =
    (0 < visibleAllOrNineSegments.length ||
      0 < visibleTotalIndicators.length ||
      0 < visibleCurrentUserIndicators.length ||
      0 < visibleRecognizedIndicators.length ||
      0 < visibleEstrangedIndicators.length ||
      0 < visibleGeneralIndicators.length) &&
    0 < visibleLoyalIndicators.length

  const hasAnyVisibleUpToLoyal =
    0 < visibleAllOrNineSegments.length ||
    0 < visibleTotalIndicators.length ||
    0 < visibleCurrentUserIndicators.length ||
    0 < visibleRecognizedIndicators.length ||
    0 < visibleEstrangedIndicators.length ||
    0 < visibleGeneralIndicators.length ||
    0 < visibleLoyalIndicators.length
  const hasAnyVisibleUpToRecognizedPositiveGeneralPositive =
    hasAnyVisibleUpToLoyal ||
    0 < visibleRecognizedPositiveGeneralPositiveIndicators.length
  const hasAnyVisibleUpToEstrangedPositiveGeneralPositive =
    hasAnyVisibleUpToRecognizedPositiveGeneralPositive ||
    0 < visibleEstrangedPositiveGeneralPositiveIndicators.length
  const hasAnyVisibleUpToGeneralPositiveLoyalPositive =
    hasAnyVisibleUpToEstrangedPositiveGeneralPositive ||
    0 < visibleGeneralPositiveLoyalPositiveIndicators.length
  const hasAnyVisibleUpToRecognizedNegativeGeneralPositive =
    hasAnyVisibleUpToGeneralPositiveLoyalPositive ||
    0 < visibleRecognizedNegativeGeneralPositiveIndicators.length
  const hasAnyVisibleUpToGeneralNegativeLoyalPositive =
    hasAnyVisibleUpToRecognizedNegativeGeneralPositive ||
    0 < visibleGeneralNegativeLoyalPositiveIndicators.length
  const hasAnyVisibleUpToRecognizedNegativeLoyalPositive =
    hasAnyVisibleUpToGeneralNegativeLoyalPositive ||
    0 < visibleRecognizedNegativeLoyalPositiveIndicators.length

  const hasRecognizedPositiveGeneralPositivePad =
    hasAnyVisibleUpToLoyal &&
    0 < visibleRecognizedPositiveGeneralPositiveIndicators.length
  const hasEstrangedPositiveGeneralPositivePad =
    hasAnyVisibleUpToRecognizedPositiveGeneralPositive &&
    0 < visibleEstrangedPositiveGeneralPositiveIndicators.length
  const hasGeneralPositiveLoyalPositivePad =
    hasAnyVisibleUpToEstrangedPositiveGeneralPositive &&
    0 < visibleGeneralPositiveLoyalPositiveIndicators.length
  const hasRecognizedNegativeGeneralPositivePad =
    hasAnyVisibleUpToGeneralPositiveLoyalPositive &&
    0 < visibleRecognizedNegativeGeneralPositiveIndicators.length
  const hasEstrangedNegativeGeneralPositivePad =
    hasAnyVisibleUpToRecognizedNegativeGeneralPositive &&
    0 < visibleEstrangedNegativeGeneralPositiveIndicators.length
  const hasGeneralNegativeLoyalPositivePad =
    hasAnyVisibleUpToRecognizedNegativeGeneralPositive &&
    0 < visibleGeneralNegativeLoyalPositiveIndicators.length
  const hasRecognizedNegativeLoyalPositivePad =
    hasAnyVisibleUpToGeneralNegativeLoyalPositive &&
    0 < visibleRecognizedNegativeLoyalPositiveIndicators.length
  const hasEstrangedNegativeLoyalPositivePad =
    hasAnyVisibleUpToRecognizedNegativeLoyalPositive &&
    0 < visibleEstrangedNegativeLoyalPositiveIndicators.length

  const columnKeys: ColumnKey[] = compact([
    'questionText',
    'answerNumber',
    'answerText',
    ...visibleAllOrNineSegments,
    hasTotalPad && 'totalPad',
    ...visibleTotalIndicators,
    hasCurrentUserPad && 'currentUserPad',
    ...visibleCurrentUserIndicators,
    hasRecognizedPad && 'recognizedPad',
    ...visibleRecognizedIndicators,
    hasEstrangedPad && 'estrangedPad',
    ...visibleEstrangedIndicators,
    hasGeneralPad && 'generalPad',
    ...visibleGeneralIndicators,
    hasLoyalPad && 'loyalPad',
    ...visibleLoyalIndicators,
    hasRecognizedPositiveGeneralPositivePad &&
      'recognizedPositiveGeneralPositivePad',
    ...visibleRecognizedPositiveGeneralPositiveIndicators,
    hasEstrangedPositiveGeneralPositivePad &&
      'estrangedPositiveGeneralPositivePad',
    ...visibleEstrangedPositiveGeneralPositiveIndicators,
    hasGeneralPositiveLoyalPositivePad && 'generalPositiveLoyalPositivePad',
    ...visibleGeneralPositiveLoyalPositiveIndicators,
    hasRecognizedNegativeGeneralPositivePad &&
      'recognizedNegativeGeneralPositivePad',
    ...visibleRecognizedNegativeGeneralPositiveIndicators,
    hasEstrangedNegativeGeneralPositivePad &&
      'estrangedNegativeGeneralPositivePad',
    ...visibleEstrangedNegativeGeneralPositiveIndicators,
    hasGeneralNegativeLoyalPositivePad && 'generalNegativeLoyalPositivePad',
    ...visibleGeneralNegativeLoyalPositiveIndicators,
    hasRecognizedNegativeLoyalPositivePad &&
      'recognizedNegativeLoyalPositivePad',
    ...visibleRecognizedNegativeLoyalPositiveIndicators,
    hasEstrangedNegativeLoyalPositivePad && 'estrangedNegativeLoyalPositivePad',
    ...visibleEstrangedNegativeLoyalPositiveIndicators,
  ])

  return columnKeys.map((key) => ({ key, width: widthMap[key] }))
}
